import React, { useState, useEffect } from 'react';
import { FileText, Eye, Edit2, CheckCircle, XCircle, Clock, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Agreement, updateAgreementStatus, updateAgreementContent } from '../lib/agreementService';

interface AgreementsManagementProps {
  brandId: string;
}

export default function AgreementsManagement({ brandId }: AgreementsManagementProps) {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
  const [editingContent, setEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editReason, setEditReason] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchAgreements();
  }, [brandId, statusFilter]);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('agreements')
        .select(`
          *,
          estimates (
            id,
            client_name,
            client_type,
            totals
          )
        `)
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAgreements(data || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      alert('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (agreementId: string, newStatus: string, notes?: string) => {
    const success = await updateAgreementStatus(agreementId, newStatus, notes);
    if (success) {
      await fetchAgreements();
      if (selectedAgreement?.id === agreementId) {
        const updated = agreements.find(a => a.id === agreementId);
        if (updated) {
          setSelectedAgreement({ ...updated, status: newStatus });
        }
      }
    } else {
      alert('Failed to update agreement status');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedAgreement || !editReason.trim()) {
      alert('Please provide a reason for the modification');
      return;
    }

    const success = await updateAgreementContent(
      selectedAgreement.id,
      editedContent,
      editReason
    );

    if (success) {
      alert('Agreement updated successfully');
      setEditingContent(false);
      setEditReason('');
      await fetchAgreements();
      const updated = agreements.find(a => a.id === selectedAgreement.id);
      if (updated) {
        setSelectedAgreement(updated);
      }
    } else {
      alert('Failed to update agreement');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      pending_review: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      signed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      superseded: { color: 'bg-red-100 text-red-800', icon: XCircle },
    };

    const badge = badges[status as keyof typeof badges] || badges.draft;
    const Icon = badge.icon;

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded flex items-center space-x-1 ${badge.color}`}>
        <Icon className="w-3 h-3" />
        <span>{status.replace('_', ' ').toUpperCase()}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (selectedAgreement) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                setSelectedAgreement(null);
                setEditingContent(false);
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to List
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            {getStatusBadge(selectedAgreement.status)}
          </div>
          <div className="flex items-center space-x-2">
            {!editingContent && selectedAgreement.status === 'draft' && (
              <>
                <button
                  onClick={() => {
                    setEditedContent(selectedAgreement.agreement_content);
                    setEditingContent(true);
                  }}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center space-x-2"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleStatusChange(selectedAgreement.id, 'approved')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Approve</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Client Information</h3>
              <div className="space-y-1">
                <p className="font-semibold">{(selectedAgreement as any).estimates?.client_name}</p>
                <p className="text-sm text-gray-600">Type: {(selectedAgreement as any).estimates?.client_type}</p>
                <p className="text-sm text-gray-600">
                  Total: ${((selectedAgreement as any).estimates?.totals?.grandTotal || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Agreement Details</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Method: {selectedAgreement.generation_method}</p>
                {selectedAgreement.claude_model && (
                  <p className="text-sm text-gray-600 flex items-center space-x-1">
                    <Sparkles className="w-3 h-3 text-purple-600" />
                    <span>Model: {selectedAgreement.claude_model}</span>
                  </p>
                )}
                <p className="text-sm text-gray-600">Version: {selectedAgreement.version}</p>
                <p className="text-sm text-gray-600">
                  Created: {new Date(selectedAgreement.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {editingContent ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agreement Content
                </label>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Modification *
                </label>
                <input
                  type="text"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Updated payment terms per client request"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEditingContent(false);
                    setEditReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editReason.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Agreement Content</h3>
              <div
                className="prose prose-sm max-w-none bg-gray-50 p-6 rounded-lg border border-gray-200 max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{
                  __html: selectedAgreement.agreement_html || selectedAgreement.agreement_content
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center space-x-2">
          <FileText className="w-5 h-5" />
          <span>Agreements Management</span>
        </h3>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="signed">Signed</option>
          <option value="superseded">Superseded</option>
        </select>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">AI-Generated Agreements</p>
            <p className="mt-1">
              Review and approve agreements generated by Claude AI. You can edit content before approval
              or after generation to ensure accuracy and compliance.
            </p>
          </div>
        </div>
      </div>

      {agreements.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No agreements found</p>
          <p className="text-sm mt-2">Agreements will appear here when clients accept proposals</p>
        </div>
      ) : (
        <div className="space-y-3">
          {agreements.map((agreement) => (
            <div
              key={agreement.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedAgreement(agreement)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {(agreement as any).estimates?.client_name}
                    </h4>
                    {getStatusBadge(agreement.status)}
                    {agreement.generation_method === 'claude_ai' && (
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded flex items-center space-x-1">
                        <Sparkles className="w-3 h-3" />
                        <span>AI Generated</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Client: {(agreement as any).estimates?.client_type}</span>
                    <span>
                      Total: ${((agreement as any).estimates?.totals?.grandTotal || 0).toLocaleString()}
                    </span>
                    <span>Created: {new Date(agreement.created_at).toLocaleDateString()}</span>
                    {agreement.signed_at && (
                      <span className="text-green-600 font-medium">
                        Signed: {new Date(agreement.signed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAgreement(agreement);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
